import dynamic from 'next/dynamic';
import Image from 'next/image';
import React from 'react';

import { FeatureShowcaseOverlayProviderContextData, useFeatureShowcaseOverlay } from './FeatureShowcaseOverlayProvider';

// Dynamically import with SSR disabled
const SlideshowCarousel = dynamic(() => import('../../../components/interactive/SlideshowCarousel'), { ssr: false });

type FeatureShowcasePanel = {
  imageUrl: string;
  label?: string;
};
type FeatureShowcaseProps = {
  title: string;
  description: string;
  panels: FeatureShowcasePanel[];
  height?: number;
};
export const FeatureShowcaseCarouselOverlay: React.FC<FeatureShowcaseProps> = React.memo((props) => {
  const { backgroundImage, setBackgroundImage } = useFeatureShowcaseOverlay();
  return <FeatureShowcaseRenderer {...props} backgroundImage={backgroundImage} setBackgroundImage={setBackgroundImage} />;
});
FeatureShowcaseCarouselOverlay.displayName = 'FeatureShowcaseCarouselOverlay';

export const FeatureShowcase: React.FC<FeatureShowcaseProps> = React.memo((props) => {
  return <FeatureShowcaseRenderer {...props} backgroundImage={undefined} setBackgroundImage={undefined} />;
});
FeatureShowcase.displayName = 'FeatureShowcase';

/************************************************************************************************
 * Helpers
 ************************************************************************************************/

type FeatureShowcaseRendererProps = FeatureShowcaseProps & Partial<FeatureShowcaseOverlayProviderContextData>;
const FeatureShowcaseRenderer: React.FC<FeatureShowcaseRendererProps> = (props) => {
  const { title, description, panels, backgroundImage, setBackgroundImage } = props;
  return (
    <div className="flex flex-col gap-4 items-center justify-center max-w-[800px] h-auto mx-auto py-12 z-10">
      {backgroundImage && <div className="absolute w-full h-full" style={{ backgroundImage }}></div>}
      <h1 className="text-center leading-none text-shadow-lg">{title}</h1>
      <p className="text-center">{description}</p>
      <SlideshowCarousel
        autoChangeWaitTimeMS={8000}
        width={800}
        buttonsWrapperClassName="top-[40px]"
        onPanelChange={(index) => setBackgroundImage && setBackgroundImage(panels[index].imageUrl)}
        panels={panels.map((panel, index) => (
          <ShowcasePanel key={`showcase-panel-${index}`} imageUrl={panel.imageUrl} label={panel.label} />
        ))}
      />
    </div>
  );
};
FeatureShowcaseRenderer.displayName = 'FeatureShowcaseRenderer';

type ShowcasePanelProps = {
  imageUrl: string;
  label: string;
};
const ShowcasePanel: React.FC<ShowcasePanelProps> = React.memo((props) => {
  const { imageUrl, label } = props;
  return (
    <div className="flex flex-col items-center gap-4">
      <Image className="md:w-full rounded-xl float-end" width={600} height={600} src={imageUrl} alt={label || 'Showcase panel'} />
      <p className="text-3xl mt-3 md:mt-2 md:text-2xl">{label}</p>
    </div>
  );
});
ShowcasePanel.displayName = 'ShowcasePanel';
